// src/components/CustomerFormModal.js
// Faz 5 (G6, 2026-08-29): hızlı müşteri ekleme = ön görüşme paritesi.
// - Telefonla mükerrer kontrol ("✓ Mevcut Müşteri" rozeti; mevcutsa yeni kayıt engellenir)
// - Araç bölümü DİREKT AÇIK; "Plaka sonradan eklenecek" (plakaBekleniyor) seçilebilir
// - Marka/model kaynağı Firestore `vehicleBrands` (statik vehicleData.js emekli)
// - Mükerrer kontroller ortak util'de, müşteri YAZILMADAN önce çalışır; hata yutulmaz.

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Box,
    FormControlLabel, Divider, CircularProgress, Autocomplete, Switch, Typography, Stack
} from '@mui/material';
import { formatPlaka, checkCustomer } from './PreInterviewFormUtils';
import { trFilterOptions } from '../utils/search';

const CustomerFormModal = ({ isOpen, onClose, onAddCustomer, onUpdateCustomer, customerToEdit }) => {
    const [isSaving, setIsSaving] = useState(false);
    // Customer fields
    const [adSoyad, setAdSoyad] = useState('');
    const [telefon, setTelefon] = useState('');
    const [email, setEmail] = useState('');
    const [adres, setAdres] = useState('');
    // Mükerrer müşteri kontrolü (ortak checkCustomer — ön görüşme paritesi)
    const [musteriId, setMusteriId] = useState(null);
    const [musteriDurumu, setMusteriDurumu] = useState('');
    // Vehicle fields — bölüm DİREKT AÇIK
    const [plaka, setPlaka] = useState('');
    const [plakaBekleniyor, setPlakaBekleniyor] = useState(false);
    const [marka, setMarka] = useState('');
    const [model, setModel] = useState('');
    const [yil, setYil] = useState('');
    const [renk, setRenk] = useState('');
    const [brands, setBrands] = useState([]);

    useEffect(() => {
        const q = query(collection(db, "vehicleBrands"), orderBy("name", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setBrands(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (isOpen && customerToEdit) {
            setAdSoyad(customerToEdit.adSoyad);
            setTelefon(customerToEdit.telefon);
            setEmail(customerToEdit.email || '');
            setAdres(customerToEdit.adres || '');
            setMusteriId(null); setMusteriDurumu('');
        } else {
            setAdSoyad(''); setTelefon(''); setEmail(''); setAdres('');
            setMusteriId(null); setMusteriDurumu('');
            setPlaka(''); setPlakaBekleniyor(false); setMarka(''); setModel(''); setYil(''); setRenk('');
        }
    }, [customerToEdit, isOpen]);

    const isEditing = !!customerToEdit;

    const handleTelefonChange = (e) => {
        const yeniTelefon = e.target.value;
        setTelefon(yeniTelefon);
        if (isEditing) return; // düzenlemede kendi kaydını "mükerrer" sanmasın
        if (yeniTelefon.length >= 10) {
            checkCustomer(yeniTelefon, setMusteriId, setMusteriDurumu, setAdSoyad, adSoyad);
        } else {
            setMusteriId(null);
            setMusteriDurumu('');
        }
    };

    const handlePlakaBekleniyorChange = (checked) => {
        setPlakaBekleniyor(checked);
        if (checked) setPlaka('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!adSoyad || !telefon) {
            alert('Ad Soyad ve Telefon alanları zorunludur.');
            return;
        }
        if (!isEditing && musteriDurumu === 'mevcut') {
            alert('Bu telefon numarası zaten kayıtlı. Yeni kayıt yerine mevcut müşteriyi kullanın (Müşteriler listesinden bulabilirsiniz).');
            return;
        }
        // Araç girişi beklenir: plaka veya "plaka sonradan" ya da hiç araç girilmemiş olmalı.
        const aracGirildi = Boolean(plaka || plakaBekleniyor || marka || model);
        if (!isEditing && aracGirildi && !plaka && !plakaBekleniyor) {
            alert("Araç için plaka girin veya 'Plaka sonradan eklenecek' seçeneğini işaretleyin.");
            return;
        }

        setIsSaving(true);
        // Faz 4.5: serbest metinler tamamı BÜYÜK HARF (e-posta hariç).
        const buyut = (s) => (s || '').toLocaleUpperCase('tr-TR');
        const customerData = { adSoyad: buyut(adSoyad), telefon, email, adres: buyut(adres) };
        const vehicleInfo = aracGirildi ? {
            plaka: plakaBekleniyor ? '' : formatPlaka(plaka).trim(),
            plakaBekleniyor,
            marka: buyut(marka), model: buyut(model), yil, renk: buyut(renk)
        } : null;

        try {
            if (customerToEdit) {
                await onUpdateCustomer(customerToEdit.id, customerData);
            } else {
                await onAddCustomer(customerData, vehicleInfo);
            }
            onClose();
        } catch (error) {
            // Modal KAPANMAZ: kullanıcı gerçek hatayı görüp düzeltebilsin (mükerrer plaka/telefon vb.)
            console.error("Müşteri/Araç kaydedilirken hata:", error);
            alert(error?.message || 'Bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditing ? 'Müşteri Bilgilerini Düzenle' : 'Yeni Müşteri Ekle'}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField label="Telefon Numarası" value={telefon} onChange={handleTelefonChange} fullWidth required placeholder="05XX XXX XX XX" />
                    {!isEditing && musteriDurumu && (
                        <Box sx={{ p: 1, borderRadius: 1, backgroundColor: musteriDurumu === 'mevcut' ? 'success.light' : 'info.light' }}>
                            <Typography variant="caption" fontWeight="bold" color={musteriDurumu === 'mevcut' ? 'success.dark' : 'info.dark'}>
                                {musteriDurumu === 'mevcut' ? `✓ Mevcut Müşteri: ${adSoyad} — yeni kayıt açılmaz` : '+ Yeni Kayıt Olacak'}
                            </Typography>
                        </Box>
                    )}
                    <TextField label="Adı Soyadı" value={adSoyad} onChange={(e) => setAdSoyad(e.target.value)} fullWidth required />
                    <TextField label="E-posta Adresi" type="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
                    <TextField label="Adres" value={adres} onChange={(e) => setAdres(e.target.value)} multiline rows={2} fullWidth />

                    {!isEditing && (
                        <>
                            <Divider sx={{ my: 1 }}>Araç Bilgileri</Divider>
                            <Box>
                                <TextField
                                    label="Plaka"
                                    placeholder="Örn: 33 ABC 123"
                                    value={plaka}
                                    onChange={(e) => setPlaka(formatPlaka(e.target.value))}
                                    fullWidth
                                    disabled={plakaBekleniyor}
                                    inputProps={{ maxLength: 12 }}
                                    helperText={plakaBekleniyor ? 'Plaka sonradan Araçlar ekranından girilecek.' : ' '}
                                />
                                <FormControlLabel
                                    control={<Switch checked={plakaBekleniyor} onChange={(e) => handlePlakaBekleniyorChange(e.target.checked)} size="small" />}
                                    label={<Typography variant="body2">Plaka sonradan eklenecek</Typography>}
                                />
                            </Box>
                            <Autocomplete
                                freeSolo
                                value={marka}
                                onChange={(e, newValue) => { setMarka(newValue || ''); setModel(''); }}
                                onInputChange={(e, newValue, reason) => { if (reason === 'input') { setMarka(newValue); setModel(''); } }}
                                options={brands.map(b => b.name)}
                                filterOptions={trFilterOptions}
                                renderInput={(params) => <TextField {...params} label="Marka" />}
                            />
                            <Autocomplete
                                freeSolo
                                value={model}
                                onChange={(e, newValue) => setModel(newValue || '')}
                                onInputChange={(e, newValue, reason) => { if (reason === 'input') setModel(newValue); }}
                                options={brands.find(b => b.name === marka)?.models || []}
                                filterOptions={trFilterOptions}
                                disabled={!marka}
                                renderInput={(params) => <TextField {...params} label="Model" />}
                            />
                            <Stack direction="row" spacing={2}>
                                <TextField label="Yıl" type="number" value={yil} onChange={e => setYil(e.target.value)} fullWidth />
                                <TextField label="Renk" value={renk} onChange={e => setRenk(e.target.value)} fullWidth />
                            </Stack>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose} disabled={isSaving}>İptal</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSaving || (!isEditing && musteriDurumu === 'mevcut')}>
                    {isSaving ? <CircularProgress size={24} /> : (isEditing ? 'Bilgileri Güncelle' : 'Kaydet')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomerFormModal;
